import { sizeNameMap } from "./sizeNameUtils";
import {
  DEFAULT_MAX_LIFESPAN_YEARS,
  getCareGuideDietTraits,
  parseCareGuideLifespanRange,
} from "./careGuideQuickFacts";

/**
 * Utility functions for fish filtering logic shared between components
 */

/**
 * Parse a temperature range string (e.g., "72-78°F", "72-78") into [min, max] numbers.
 * Returns null if unparseable.
 */
const RANGE_SEPARATOR_REGEX = /(\d+\.?\d*)\s*(?:-|–|—|to)\s*(\d+\.?\d*)/i;
const SINGLE_VALUE_REGEX = /^(\d+\.?\d*)$/;

const parseRangeString = (value) => {
  if (!value) return null;
  const cleaned = String(value)
    .replace(/[^0-9.\-–—a-zA-Z\s]/g, " ")
    .trim()
    .toLowerCase();

  const match = cleaned.match(RANGE_SEPARATOR_REGEX);
  if (match) {
    return [parseFloat(match[1]), parseFloat(match[2])];
  }

  const single = cleaned.match(SINGLE_VALUE_REGEX);
  if (single) {
    const val = parseFloat(single[1]);
    return [val, val];
  }

  return null;
};

export function parseTemperatureRange(tempStr) {
  return parseRangeString(tempStr);
}

export function parsePhRange(phStr) {
  return parseRangeString(phStr);
}

/**
 * Check if a fish/species matches care requirement filters using a care guide map.
 * Species without care guide data pass through (are not excluded) unless
 * hideWithoutCareGuide is true.
 */
export function fishMatchesCareRequirements(
  fish,
  careGuidesMap,
  careFilters
) {
  const {
    minTankSizeMax,
    maxSizeMax,
    temperatureRange,
    phRange,
    careLevels,
    reefSafeValues,
    plantSafeValues,
    temperamentValues,
    dietTraits,
    lifespanRange,
    maxLifespanYears,
  } = careFilters;

  const hasActiveFilter =
    minTankSizeMax != null ||
    maxSizeMax != null ||
    temperatureRange != null ||
    phRange != null ||
    (careLevels && careLevels.length > 0) ||
    (reefSafeValues && reefSafeValues.length > 0) ||
    (plantSafeValues && plantSafeValues.length > 0) ||
    (temperamentValues && temperamentValues.length > 0) ||
    (dietTraits && dietTraits.length > 0) ||
    lifespanRange != null;

  if (!hasActiveFilter) return true;

  const key = fish.scientific_name?.toLowerCase();
  const summary = key ? careGuidesMap[key] : null;

  if (!summary) return false;

  if (minTankSizeMax != null && summary.min_tank_size_gallons) {
    if (summary.min_tank_size_gallons > minTankSizeMax) return false;
  } else if (minTankSizeMax != null && !summary.min_tank_size_gallons) {
    return false;
  }

  if (maxSizeMax != null && summary.max_size_inches !== undefined && summary.max_size_inches !== null) {
    if (Number(summary.max_size_inches) > maxSizeMax) return false;
  } else if (maxSizeMax != null && (summary.max_size_inches === undefined || summary.max_size_inches === null)) {
    return false;
  }

  if (temperatureRange) {
    const speciesTemp = parseTemperatureRange(summary.temperature_range);
    if (!speciesTemp) return false;
    if (speciesTemp[1] < temperatureRange[0] || speciesTemp[0] > temperatureRange[1]) return false;
  }

  if (phRange) {
    const speciesPh = parsePhRange(summary.ph_range);
    if (!speciesPh) return false;
    if (speciesPh[1] < phRange[0] || speciesPh[0] > phRange[1]) return false;
  }

  if (careLevels && careLevels.length > 0) {
    if (!summary.care_level) return false;
    if (!careLevels.includes(summary.care_level.toLowerCase())) return false;
  }

  if (reefSafeValues && reefSafeValues.length > 0) {
    if (!summary.reef_safe) return false;
    if (!reefSafeValues.includes(summary.reef_safe.toLowerCase())) return false;
  }

  if (plantSafeValues && plantSafeValues.length > 0) {
    if (!summary.plant_safe) return false;
    if (!plantSafeValues.includes(summary.plant_safe.toLowerCase())) return false;
  }

  if (temperamentValues && temperamentValues.length > 0) {
    if (!summary.temperament) return false;
    if (!temperamentValues.includes(summary.temperament.toLowerCase())) return false;
  }

  if (dietTraits && dietTraits.length > 0) {
    if (!summary.diet) return false;
    const speciesDietTraits = getCareGuideDietTraits(summary.diet);
    if (!dietTraits.every((trait) => speciesDietTraits.includes(trait))) {
      return false;
    }
  }

  if (lifespanRange) {
    if (!summary.lifespan) return false;
    const speciesLifespan = parseCareGuideLifespanRange(
      summary.lifespan,
      maxLifespanYears || DEFAULT_MAX_LIFESPAN_YEARS
    );
    if (!speciesLifespan) return false;
    if (
      speciesLifespan[1] < lifespanRange[0] ||
      speciesLifespan[0] > lifespanRange[1]
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a fish matches a specific taxonomic category
 * @param {Object} fish - The fish object to check
 * @param {Object} category - The category object to match against
 * @returns {boolean} - True if fish matches the category
 */
export function fishMatchesCategory(fish, category) {
  if (!category) {
    return false;
  }
  const categoryTypeName = category.rank_type + "_name";
  const scientificName = category.scientific_name;
  return fish[categoryTypeName] === scientificName;
}

/**
 * Check if a fish matches a category filter by common name
 * @param {Object} fish - The fish object to check
 * @param {string} categoryFilter - The category common name
 * @param {Array} categoryData - Array of category objects
 * @returns {boolean} - True if fish matches the category or if no category filter is applied
 */
export function fishMatchesCategoryFilter(fish, categoryFilter, categoryData) {
  if (!categoryFilter || !categoryData || categoryData.length === 0) {
    return true;
  }
  const category = categoryData.find(
    (category) => category.common_name === categoryFilter
  );
  return fishMatchesCategory(fish, category);
}

/**
 * Check if a fish matches size filters
 * @param {Object} fish - The fish object to check
 * @param {Array} sizeFilter - Min and max numeric size range [min, max]
 * @param {boolean} includeNumericSizes - Whether to include numeric sizes
 * @param {boolean} includeNonNumericSizes - Whether to include non-numeric sizes
 * @param {Array} nonNumericSizeFilter - Array of allowed non-numeric size names
 * @returns {boolean} - True if fish matches the size filters
 */
export function fishMatchesSizeFilter(
  fish,
  sizeFilter,
  includeNumericSizes,
  includeNonNumericSizes,
  nonNumericSizeFilter
) {
  const sizeNumber = Number(fish.size);
  if (isNaN(sizeNumber) || sizeNumber < 0) {
    return (
      includeNonNumericSizes &&
      (!nonNumericSizeFilter || nonNumericSizeFilter.includes(sizeNameMap[fish.size]))
    );
  }
  return (
    includeNumericSizes &&
    sizeNumber >= sizeFilter[0] &&
    sizeNumber <= sizeFilter[1]
  );
}

/**
 * Check if fish matches freshwater/saltwater filters
 * @param {Object} fish - The fish object to check
 * @param {boolean} freshwaterFilter - Include freshwater fish
 * @param {boolean} saltwaterFilter - Include saltwater fish
 * @returns {boolean} - True if fish matches water type filters
 */
export function fishMatchesFreshSaltFilter(
  fish,
  freshwaterFilter,
  saltwaterFilter
) {
  if (freshwaterFilter && saltwaterFilter) {
    // If both filters are enabled, include all fish, even those with null fresh/salt
    return true;
  }
  return (
    (freshwaterFilter && fish.is_freshwater) ||
    (saltwaterFilter && fish.is_saltwater)
  );
}

/**
 * Check if fish matches taxonomic filters
 * @param {Object} fish - The fish object to check
 * @param {string} genusFilter - Genus filter
 * @param {string} familyFilter - Family filter
 * @param {string} orderFilter - Order filter
 * @param {string} classFilter - Class filter
 * @param {string} phylumFilter - Phylum filter
 * @returns {boolean} - True if fish matches all applied taxonomic filters
 */
export function fishMatchesTaxonomicFilters(
  fish,
  genusFilter,
  familyFilter,
  orderFilter,
  classFilter,
  phylumFilter
) {
  return (
    (!genusFilter || fish.genus_name === genusFilter) &&
    (!familyFilter || fish.family_name === familyFilter) &&
    (!orderFilter || fish.order_name === orderFilter) &&
    (!classFilter || fish.class_name === classFilter) &&
    (!phylumFilter || fish.phylum_name === phylumFilter)
  );
}

/**
 * Apply all filters to check if a fish should be shown
 * @param {Object} fish - The fish object to check
 * @param {Object} filters - Object containing all filter settings
 * @param {Object} searchResults - Map of scientific names to search ranks
 * @param {Object} categoryData - Array of category objects
 * @returns {boolean} - True if fish passes all filters
 */
export function fishMatchesAllFiltersExcludingSize(
  fish,
  filters,
  searchResults,
  categoryData,
  userFavorites = [],
  userSubscriptions = []
) {
  // Skip specific store filter check for deals carousel compatibility
  const {
    searchTerm,
    scientificFilter,
    genusFilter,
    familyFilter,
    orderFilter,
    classFilter,
    phylumFilter,
    categoryFilter,
    allowedStores,
    freshwaterFilter,
    saltwaterFilter,
    sizeFilter,
    includeNumericSizes,
    includeNonNumericSizes,
    nonNumericSizeFilter,
    favoritesOnly,
    notificationsOnly,
  } = filters;

  return (
    // Search filter
    (searchTerm === "" || searchResults[fish.scientific_name]) &&
    // Scientific name filter
    (scientificFilter === "" || fish.scientific_name === scientificFilter) &&
    // Store filter (only applied if provided)
    (allowedStores ? allowedStores.includes(fish.source) : true) &&
    // Favorites filter
    (!favoritesOnly || fishIsFavorited(fish, userFavorites)) &&
    // Notifications filter
    (!notificationsOnly || fishHasNotifications(fish, userSubscriptions)) &&
    // Taxonomic filters
    fishMatchesTaxonomicFilters(
      fish,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter
    ) &&
    // Category filter
    fishMatchesCategoryFilter(fish, categoryFilter, categoryData) &&
    // Fresh/salt water filter
    fishMatchesFreshSaltFilter(fish, freshwaterFilter, saltwaterFilter)
    // Size filters are applied separately in some cases
  );
}

/**
 * Apply all filters to check if a fish should be shown
 * @param {Object} fish - The fish object to check
 * @param {Object} filters - Object containing all filter settings
 * @param {Object} searchResults - Map of scientific names to search ranks
 * @param {Array} categoryData - Array of category objects
 * @returns {boolean} - True if fish passes all filters
 */
export function applyAllFilters(
  fish,
  filters,
  searchResults,
  categoryData,
  userFavorites = [],
  userSubscriptions = [],
  reefWizardRecommendations = []
) {
  const {
    searchTerm,
    scientificFilter,
    sizeFilter,
    includeNumericSizes,
    includeNonNumericSizes,
    nonNumericSizeFilter,
    genusFilter,
    familyFilter,
    orderFilter,
    classFilter,
    phylumFilter,
    categoryFilter,
    allowedStores,
    freshwaterFilter,
    saltwaterFilter,
    favoritesOnly,
    notificationsOnly,
    reefWizardOnly,
    reefWizardTankFilter,
  } = filters;

  // Search filter
  if (searchTerm && !searchResults[fish.scientific_name]) {
    return false;
  }

  // Scientific name filter
  if (scientificFilter && fish.scientific_name !== scientificFilter) {
    return false;
  }

  // Store filter
  if (allowedStores && !allowedStores.some((store) => store === fish.source)) {
    return false;
  }

  // Favorites filter
  if (favoritesOnly && !fishIsFavorited(fish, userFavorites)) {
    return false;
  }

  // Notifications filter
  if (notificationsOnly && !fishHasNotifications(fish, userSubscriptions)) {
    return false;
  }

  // Reef Wizard recommendations filter
  if (reefWizardOnly && !fishIsReefWizardRecommended(fish, reefWizardRecommendations, reefWizardTankFilter)) {
    return false;
  }

  // Size filter
  if (
    !fishMatchesSizeFilter(
      fish,
      sizeFilter,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter
    )
  ) {
    return false;
  }

  // Freshwater/saltwater filter
  if (!fishMatchesFreshSaltFilter(fish, freshwaterFilter, saltwaterFilter)) {
    return false;
  }

  // Taxonomic filters
  if (
    !fishMatchesTaxonomicFilters(
      fish,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter
    )
  ) {
    return false;
  }

  // Category filter
  if (!fishMatchesCategoryFilter(fish, categoryFilter, categoryData)) {
    return false;
  }

  // Care requirements filter
  if (filters.careGuidesMap && filters.careFilters) {
    if (!fishMatchesCareRequirements(fish, filters.careGuidesMap, filters.careFilters)) {
      return false;
    }
  }

  // Has care guide filter
  if (filters.hasCareGuide && filters.hasCareGuide !== "all" && filters.careGuidesMap) {
    const key = fish.scientific_name?.toLowerCase();
    const hasGuide = key ? !!filters.careGuidesMap[key] : false;
    if (filters.hasCareGuide === "yes" && !hasGuide) return false;
    if (filters.hasCareGuide === "no" && hasGuide) return false;
  }

  return true;
}

/**
 * Check if a review contains any fish that matches the filters
 * @param {Object} review - The review object to check
 * @param {Object} filters - Object containing all filter settings
 * @param {Object} searchResults - Map of scientific names to search ranks
 * @param {Array} categoryData - Array of category objects
 * @returns {boolean} - True if review contains at least one matching fish
 */
export function reviewMatchesFilters(
  review,
  filters,
  searchResults,
  categoryData
) {
  if (!review.fish_reviews || review.fish_reviews.length === 0) {
    const {
      searchTerm,
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
    } = filters;
    if (scientificFilter || genusFilter || familyFilter || orderFilter || classFilter || phylumFilter) {
      return false;
    }

    if (allowedStores && !allowedStores.some((store) => store === review.store_url)) {
      return false;
    }

    if (searchTerm && review.product_reviews && review.product_reviews.length > 0) {
      const query = searchTerm.toLowerCase();
      const productText = review.product_reviews
        .map((productReview) => {
          const bucket = productReview.product_bucket || {};
          return [
            bucket.display_name,
            bucket.brand,
            bucket.product_line,
            bucket.model,
            bucket.category?.name,
            productReview.notes,
            review.review_text,
          ].filter(Boolean).join(" ");
        })
        .join(" ")
        .toLowerCase();
      return productText.includes(query);
    }

    return true;
  }

  // Convert fish reviews to a format compatible with fish filtering
  const fishFromReviews = review.fish_reviews
    .map((fishReview) => {
      const species = fishReview.species_listing?.species;
      if (!species) return null;

      return {
        scientific_name: species.scientific_name,
        common_name: species.common_name,
        size: fishReview.species_listing?.size || "-1",
        source: review.store_url, // Use the review's store as the source
        is_freshwater: species.is_freshwater,
        is_saltwater: species.is_saltwater,
        is_brackish: species.is_brackish,
        genus_name: species.genus?.genus_name,
        family_name: species.genus?.family_name,
        order_name: species.genus?.order_name,
        class_name: species.genus?.class_name,
        phylum_name: species.genus?.phylum_name,
        kingdom_name: species.genus?.kingdom_name,
        organism_type: species.genus?.organism_type,
      };
    })
    .filter(Boolean);

  // Check if any fish in the review matches the filters
  return fishFromReviews.some((fish) =>
    applyAllFilters(fish, filters, searchResults, categoryData)
  );
}

/**
 * Check if a fish is in the user's favorites
 * @param {Object} fish - The fish object to check
 * @param {Array} userFavorites - Array of user favorite objects with scientific_name
 * @returns {boolean} - True if fish is favorited
 */
export function fishIsFavorited(fish, userFavorites) {
  if (!userFavorites || userFavorites.length === 0) {
    return false;
  }
  return userFavorites.some(
    (favorite) =>
      favorite.species &&
      favorite.species.scientific_name.toLowerCase() === fish.scientific_name.toLowerCase()
  );
}

/**
 * Check if a fish has active notifications
 * @param {Object} fish - The fish object to check
 * @param {Array} userSubscriptions - Array of user subscription objects with scientific_name
 * @returns {boolean} - True if fish has active notifications
 */
export function fishHasNotifications(fish, userSubscriptions) {
  if (!userSubscriptions || userSubscriptions.length === 0) {
    return false;
  }
  return userSubscriptions.some(
    (subscription) => 
      subscription.listing_type !== "product" &&
      subscription.scientific_name.toLowerCase() === fish.scientific_name.toLowerCase() &&
      subscription.is_active
  );
}

/**
 * Check if a fish matches reef wizard recommendations
 * @param {Object} fish - The fish object to check
 * @param {Array} reefWizardRecommendations - Array of recommended species objects
 * @param {number|null} tankFilter - Optional tank ID to filter recommendations by
 * @returns {boolean} - True if fish was recommended by reef wizard
 */
export function fishIsReefWizardRecommended(fish, reefWizardRecommendations, tankFilter = null) {
  if (!reefWizardRecommendations || reefWizardRecommendations.length === 0) {
    return false;
  }
  
  return reefWizardRecommendations.some((recommendation) => {
    // Check if scientific name matches
    const nameMatches = recommendation.scientific_name.toLowerCase() === fish.scientific_name.toLowerCase();
    
    // If no tank filter, just check the name
    if (tankFilter === null) {
      return nameMatches;
    }
    
    // If tank filter is specified, also check if the recommendation was for that tank
    return nameMatches && recommendation.tank_ids && recommendation.tank_ids.includes(parseInt(tankFilter));
  });
}

/**
 * Get the recommendation score for a fish (based on most recent recommendation)
 * Used for sorting by reef wizard recommendation recency
 * @param {Object} fish - The fish object to check
 * @param {Array} reefWizardRecommendations - Array of recommended species objects
 * @returns {number} - Timestamp score (higher = more recent), 0 if not recommended
 */
export function getReefWizardRecommendationScore(fish, reefWizardRecommendations) {
  if (!reefWizardRecommendations || reefWizardRecommendations.length === 0) {
    return 0;
  }
  
  const recommendation = reefWizardRecommendations.find(
    (rec) => rec.scientific_name.toLowerCase() === fish.scientific_name.toLowerCase()
  );
  
  if (!recommendation || !recommendation.most_recent_recommendation) {
    return 0;
  }
  
  // Return timestamp as score (higher = more recent)
  return new Date(recommendation.most_recent_recommendation).getTime();
}
